import { useBreakpointIndex } from '@theme-ui/match-media';
import { orderBy } from 'lodash';
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { AppStateContext } from 'api/AppStateContext';
import DiseaseActivityApi from 'api/DiseaseActivityApi';
import DiseaseApi from 'api/DiseaseApi';
import * as dto from 'client/dto';
import { RiskDirectionType } from 'models/RiskCategories';
import { Geoname } from 'utils/constants';
import { MapAggregationCat2Request } from 'utils/modelHelpers';
import { isMobile } from 'utils/responsive';

import esriMap from 'map';

import { IPanelProps } from 'components/Panel';
import { ActivePanel } from 'components/SidebarView/sidebar-types';

import EventDetailPanelDisplay from './EventDetailPanelDisplay';

type EventDetailPanelContainerProps = IPanelProps & {
  activePanel: ActivePanel;
  geonameId?: number;
  disactIds: dto.CountryDiseaseActivityKey;
  onEventDetailsLoad: (val: dto.GetEventModel) => void;
  onEventDetailsNotFound: () => void;
  onRiskParametersClicked: () => void;
  onGoToActivityHistory: (activityDate?: string) => void;
  isRiskParametersSelected: boolean;
  onSelectedRiskTypeChanged: (val: RiskDirectionType) => void;
  seedRiskDirectionType?: RiskDirectionType;
};

const EventDetailPanelContainer: React.FC<EventDetailPanelContainerProps> = ({
  activePanel,
  geonameId,
  disactIds,
  onEventDetailsLoad,
  onEventDetailsNotFound,
  onRiskParametersClicked,
  onGoToActivityHistory,
  isRiskParametersSelected,
  onSelectedRiskTypeChanged,
  seedRiskDirectionType = 'importation',
  isMinimized,
  onMinimize,
  onClose,
}) => {
  const [event, setEvent] = useState<dto.GetEventModel>({
    caseCounts: {},
    importationRisk: null,
    exportationRisk: null,
    eventInformation: {},
    eventLocations: [],
    airports: {
      sourceAirports: [],
      destinationAirports: [],
    },
    articles: [],
    isLocal: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [activeRiskType, setActiveRiskType] = useState<RiskDirectionType>(seedRiskDirectionType);
  const { appState } = useContext(AppStateContext);

  useEffect(() => {
    onSelectedRiskTypeChanged && onSelectedRiskTypeChanged(activeRiskType);
  }, [activeRiskType]);

  const handleZoomToLocation = () => {
    esriMap.setExtentToEventDetail();
  };

  const loadEvent = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    DiseaseActivityApi.DiseaseActivityController_GetDiseaseActivity({
      ...(geonameId !== Geoname.GLOBAL_VIEW && { userLocationId: geonameId }),
      diseaseId: disactIds.diseaseId,
      speciesId: disactIds.speciesId,
      countryId: disactIds.countryId,
      activityStartDate: appState.startDate,
    })
      .then(data => {
        // eslint-disable-next-line no-param-reassign
        data.articles = orderBy(data.articles, ['publishedDate'], 'desc');
        setEvent(data);
        onEventDetailsLoad(data);
      })
      .catch(e => {
        setHasError(true);
        if (e.response && (e.response.status === 404 || e.response.status === 400)) {
          onEventDetailsNotFound();
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (disactIds) {
      loadEvent();
    }
  }, [disactIds, appState.startDate]);

  const isMobileDevice = isMobile(useBreakpointIndex());
  if (isMobileDevice && activePanel !== 'EventDetailPanel') {
    return null;
  }

  if (!disactIds) {
    return null;
  }

  return (
    <EventDetailPanelDisplay
      isLoading={isLoading}
      activePanel={activePanel}
      event={event}
      hasError={hasError}
      onClose={onClose}
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      onZoomToLocation={handleZoomToLocation}
      handleRetryOnClick={loadEvent}
      onRiskParametersClicked={onRiskParametersClicked}
      onGoToActivityHistory={onGoToActivityHistory}
      isRiskParametersSelected={isRiskParametersSelected}
      selectedRiskType={activeRiskType}
      onSelectedRiskTypeChanged={setActiveRiskType}
      onNeedApiGetDiseaseActivity={(startDate, endDate, aggregationCategory) => {
        return DiseaseApi.DiseaseController_GetTemporalActivity({
          ...MapAggregationCat2Request[aggregationCategory],
          diseaseId: disactIds?.diseaseId,
          speciesId: disactIds?.speciesId,
          countryId: disactIds?.countryId,
          includeProximalActivity: false,
          activityStartDate: startDate ? startDate.toISOString() : undefined,
          activityEndDate: endDate ? endDate.toISOString() : undefined,
        });
      }}
    />
  );
};

export default EventDetailPanelContainer;
